import { Typography } from '@/components/app'
import { SectionTitle } from './SectionTitle'
import { AuthenticProduct, BISHallMark, Cards, Stack } from '../icons/customIcons'
import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { fadeUp, scaleIn, staggerContainer, viewportOnce } from '@/lib/animations'

interface TrustFeature {
  icon: React.ReactNode
  title: string
  description: string
}

interface Stat {
  value: string
  label: string
  numericValue?: number
  suffix?: string
  prefix?: string
}

const trustFeatures: TrustFeature[] = [
  {
    icon: <BISHallMark/>,
    title: 'BIS Hallmark Certified',
    description: 'Every piece of jewellery is certified for purity and authenticity by BIS standards.',
  },
  {
    icon: <Cards/>,
    title: 'Secure Payment Gateway',
    description: '256-bit SSL encryption ensures your financial data stays private and protected.',
  },
  {
    icon: <Stack/>,
    title: 'Browse Jewellery Collections',
    description: 'Hassle-free 15-day return and exchange policy on all jewellery purchases.',
  },
  {
    icon: <AuthenticProduct/>,
    title: 'Authentic Product Guarantee',
    description: '100% genuine jewellery with certificate of authenticity for every product.',
  },
]

const stats: Stat[] = [
  {
    value: '4.9 / 5',
    label: 'App Rating',
    numericValue: 4.9,
    suffix: ' / 5',
  },
  {
    value: '10,000+',
    label: 'Happy Customers',
    numericValue: 10000,
    suffix: '+',
  },
  {
    value: '50,000+',
    label: 'Products Delivered',
    numericValue: 50000,
    suffix: '+',
  },
  {
    value: '100%',
    label: 'Certified Products',
    numericValue: 100,
    suffix: '%',
  },
]

// Counter Animation Hook
function useCounterAnimation(targetValue: number, duration: number = 2000, isVisible: boolean) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isVisible) {
      setCount(0)
      return
    }

    let startTime: number | null = null
    const startValue = 0
    let animationFrameId: number

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime
      const progress = Math.min((currentTime - startTime) / duration, 1)
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const currentCount = startValue + (targetValue - startValue) * easeOutQuart
      setCount(currentCount)
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate)
      }
    }

    animationFrameId = requestAnimationFrame(animate)
    return () => { if (animationFrameId) cancelAnimationFrame(animationFrameId) }
  }, [targetValue, duration, isVisible])

  return count
}

// Stat Counter Component
function StatCounter({ stat, isVisible }: { stat: Stat; isVisible: boolean }) {
  const count = useCounterAnimation(stat.numericValue || 0, 2000, isVisible)

  const formatValue = () => {
    if (!stat.numericValue) return stat.value
    if (stat.suffix === ' / 5') return count.toFixed(1) + stat.suffix
    else if (stat.numericValue >= 1000) return Math.floor(count).toLocaleString() + (stat.suffix || '')
    else return Math.floor(count) + (stat.suffix || '')
  }

  return (
    <div className='border border-[#CBC1BC50] w-1/2 md:w-1/4 py-1'>
      <div className={`text-center bg-primary h-full relative z-1`}>
        <Typography variant="h2" className="text-[#CBC1BC]">
          {formatValue()}
        </Typography>
        <Typography variant="body2" className="text-[#CBC1BC]">
          {stat.label}
        </Typography>
      </div>
    </div>
  )
}

export function TrustSecuritySection() {
  const statsRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { setIsVisible(entry.isIntersecting) },
      { threshold: 0.3 }
    )
    if (statsRef.current) observer.observe(statsRef.current)
    return () => { if (statsRef.current) observer.unobserve(statsRef.current) }
  }, [])

  return (
    <section id="trust-security" className="py-6 md:py-10 lg:py-12">
      <div className="wrapper">
        {/* Section Title */}
        <motion.div
          className="mb-12 md:mb-16"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <SectionTitle
            badge="TRUST & SECURITY"
            title="Shop with Complete Peace of Mind"
            subtitle="Your trust is our most precious asset. We ensure the highest standards of quality and security."
            centered
          />
        </motion.div>

        {/* Trust Features Grid */}
        <motion.div
          className='bg-secondary flex flex-wrap relative after:content-[""] after:absolute after:w-full after:h-full after:inset-0 after:border after:border-background'
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          {trustFeatures.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className='w-full md:w-1/2 border border-transparent relative z-1'
              whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
            >
              <div className="space-y-2 p-6 bg-background transition-shadow h-full">
                <motion.div
                  className="w-12 h-12 rounded-[12px] border border-border-light flex items-center justify-center text-black"
                  whileHover={{ scale: 1.1, rotate: 5, transition: { duration: 0.2 } }}
                >
                  {feature.icon}
                </motion.div>
                <div className="space-y-2">
                  <Typography variant="h6" className="font-semibold">
                    {feature.title}
                  </Typography>
                  <Typography color="muted">
                    {feature.description}
                  </Typography>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Section */}
        <motion.div
          className="mt-4 md:mt-5"
          ref={statsRef}
          variants={scaleIn}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <div className="bg-primary rounded-3xl px-5 md:px-8 xl:px-16 py-3 md:p-4 xl:py-6 ">
            <div className='flex flex-wrap relative after:content-[""] after:absolute after:w-full after:h-full after:inset-0 after:border after:border-primary'>
              {stats.map((stat, index) => (
                <StatCounter key={index} stat={stat} isVisible={isVisible} />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
